/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import sass from "./desc.module.sass";
import InputTemp from "components-new/InputTemp";
import TextArea from "components-new/Textarea";
import PortalWrapper from "components-new/PortalWrapper";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { CreateContext } from "pages/StepPages/StepFour/One/Tables/Tables";
import { useParams } from "react-router-dom";
import { IDescriptionTechTableNew } from "pages/StepPages/StepFour/One/Tables/models";
import parentSass from "pages/StepPages/StepFour/One/Tables/TableNavigate/nav.module.sass";
import { ReactComponent as LoadingIcon } from "images/load.svg";
import {
	AcceptImage,
	ConfirmRemoveTableItem,
	ConfirmRemoveTechModal,
	fromTable,
	getKeyByValue,
	ModalTable,
	ModalTech,
} from "pages/StepPages/StepFour/One/Tables/TableNavigate/DescriptionTable/Modals";
import Accordion from "pages/StepPages/StepFour/One/Tables/TableNavigate/DescriptionTable/Accordion/Accordion";
import AccordionItem from "pages/StepPages/StepFour/One/Tables/TableNavigate/DescriptionTable/Accordion/AccordionItem";
import { usePaginationWithIntersection } from "hooks/usePaginationWithIntersection";
import AddImage from "./AddImage";
import { ChangeActiveTabContext } from "../TableNavigate";
import { organizationAPI } from "libs/apiClient/Organization";
import { SystemDescriptionParams, SystemDescriptionResponse } from "libs/apiClient/data-contracts";

export interface ITableContent {
	accessType: string;
	category: string;
	id: number;
	level: string;
	name: string;
	permission: string;
}

const initial: SystemDescriptionResponse = {
	categorySignificance: "",
	id: 0,
	security: "",
	architecture: "",
	// groupDescription: "",
	interfaceDescription: "",
	name: "",
	processes: "",
	file: null,
	purpose: "",
	securityLevel: "",
	structure: "",
	structureFile: null,
};

/**
 * @description это не совсем таблица, скорее форма для заполнения
 * @constructor
 */
const DescriptionTable = () => {
	/**
	 * данные из контекста, контейнер для прокидывания дополнительных элементов в шапку и данные полученные с сервера
	 */
	const { container, requiredFields, confirm, currentThread, description, selectFile, openConfirm, checkTables } =
		useContext(CreateContext);
	const { updateTabs } = useContext(ChangeActiveTabContext);

	const [load, setLoad] = useState(false);
	const params = useParams();

	const [image, setImage] = useState<{ file?: File; mode: "ARCHITECTURE" | "STRUCTURE" }>({
		file: undefined,
		mode: "STRUCTURE",
	});

	const [accept, setAccept] = useState(false);
	const [techList, setTechList] = useState<IDescriptionTechTableNew[]>([]);

	const [tech, setTech] = useState<{ active: boolean; data: IDescriptionTechTableNew | null }>({
		active: false,
		data: null,
	});

	const [tableContent, setTableContent] = useState<ITableContent[]>([]);
	const [removeTableItem, setRemoveTableItem] = useState<{ active: boolean; id: number | null }>({
		active: false,
		id: null,
	});

	const submitHandlerTable = useCallback(
		(upload: Omit<ITableContent, "id"> & { id?: number }, mode: "edit" | "create") => {
			if (mode === "create") {
				const { id, ...data } = upload;
				organizationAPI.objectSystemGroupsCreate(Number(params.threadId), data).then(({ data }) => {
					setTableContent((prevState) => [...prevState, data]);
					updateTabs("SYSTEM");
					setTable({ active: false, data: null });
				});
			} else {
				const { id, ...data } = upload;
				organizationAPI.objectSystemGroupUpdate(id!, data).then(({ data }) => {
					setTableContent((prevState) => prevState.map((_) => (_.id === data.id ? data : _)));
					updateTabs("SYSTEM");
					setTable({ active: false, data: null });
				});
			}
		},
		//eslint-disable-next-line
		[params.threadId],
	);
	const removeHandlerTable = (id: number) => {
		organizationAPI.objectSystemGroupDelete(id).then(() => {
			setTableContent((prevState) => prevState.filter((_) => _.id !== id));
			updateTabs("SYSTEM");
			setRemoveTableItem({ active: false, id: null });
		});
	};

	const [table, setTable] = useState<{
		active: boolean;
		data: ITableContent | null;
	}>({
		active: false,
		data: null,
	});

	useEffect(() => {
		organizationAPI.objectSystemDetail(+params.threadId!).then(({ data }) => {
			setDesc(data)
		});
		//eslint-disable-next-line
	},[]);

	const [removeTech, setRemoveTech] = useState(false);
	const [currentTech, setCurrentTech] = useState<IDescriptionTechTableNew | null>(null);

	/**
	 * флаг для отслеживания наличия контейнера, необходим только для первой вкладки, так как изначально нет контейнера
	 */
	const [flag, setFlag] = useState(false);
	/**
	 * стэйт для начальных данных формы
	 */
	const [desc, setDesc] = useState<SystemDescriptionResponse>(initial);

	/**
	 * если контейнер в контексте присутствует, то флаг видимости переключается на "видимый"
	 */
	useEffect(() => {
		if (container && container.current) {
			setFlag(true);
		}
	}, [container]);

	/**
	 * если данные с сервера есть, то устанавливаются они
	 */
	useEffect(() => {
		setLoad(true);
		if (description) {
			setDesc(description);
			setLoad(false);
		}
		//eslint-disable-next-line
	}, [description]);

	/**
	 * функция для изменения полей формы
	 */
	const changeField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setDesc((prevState) => ({ ...prevState, [name]: value }));
	};
	/**
	 * отправка данных на сервер
	 */
	const postFetchData = () => {
		if (!confirm) {
			const fetch: SystemDescriptionParams = {
				// groupDescription: desc.groupDescription || null,
				structure: desc.structure || null,
				security: desc.security || null,
				categorySignificance: desc.categorySignificance || null,
				architecture: desc.architecture || null,
				interfaceDescription: desc.interfaceDescription || null,
				processes: desc.processes || null,
				purpose: desc.purpose || null,
				securityLevel: desc.securityLevel || null,
				name: desc.name || "",
			};
			organizationAPI.objectSystemCreate(Number(params.threadId), fetch).then(() => {
				updateTabs("SYSTEM");
			});
		}
	};

	const ref = useRef<HTMLInputElement>(null);

	const focus = () => {
		if (confirm) {
			openConfirm();
		}
	};
	const approvedDoc = useMemo(() => {
		return currentThread && currentThread.threatStatus === "APPROVED";
	}, [currentThread]);

	const [target, setTarget] = useState<HTMLSpanElement | null>(null);

	const menuRefStructure = useRef<HTMLDivElement | null>(null);
	const menuRefArchitecture = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		function handleClickOutside(event: any) {
			if (menuRefStructure.current && !menuRefArchitecture.current  && target && !menuRefStructure.current?.contains(event.target)) {
				setTarget(null);
			}else if (menuRefArchitecture.current && !menuRefStructure.current && target && !menuRefArchitecture.current?.contains(event.target)) {
				setTarget(null);
			}
		}

		document.addEventListener("mouseup", handleClickOutside);
		console.log(menuRefStructure.current);
		console.log(menuRefArchitecture.current);
		return () => {
			document.removeEventListener("mouseup", handleClickOutside);
		};
		//eslint-disable-next-line
	}, [menuRefArchitecture.current,menuRefStructure.current, target]);

	const createOrUpdateItemTech = useCallback(
		(data: IDescriptionTechTableNew & { fullName?: string }, mode: "create" | "edit" = "create") => {
			if (desc.id) {
				if (mode === "create") {
					let { id, fullName, ...rest } = data;
					rest.from = getKeyByValue(fromTable, rest.from)!;
					organizationAPI.objectSystemMeansCreate(desc.id, rest).then(() => {
						setTech({ active: false, data: null });
						setParamsMean((prevState) => ({ ...prevState, part: 1 }));
						updateTabs("SYSTEM");
						setIsFinally(false);
						setFetch(true);
					});
				} else {
					const { id, fullName, ...rest } = data;
					const newData = {
						...rest,
						from: getKeyByValue(fromTable, rest.from)!,
					};
					organizationAPI.objectSystemMeanUpdate(id!, newData).then(() => {
						setTech({ active: false, data: null });
						updateTabs("SYSTEM");
						setParamsMean((prevState) => ({ ...prevState, part: 1 }));
						setIsFinally(false);
						setFetch(true);
					});
				}
			}
		},
		//eslint-disable-next-line
		[desc],
	);

	const removeItemTech = (id: number) => {
		organizationAPI.objectSystemMeanDelete(id).then(() => {
			setRemoveTech(false);
			setTechList((prevState) => prevState.filter((_) => _.id !== id));
			updateTabs("SYSTEM");
			setCurrentTech(null);
		});
	};

	const getImageArchitecure = ()=> {
		organizationAPI.objectSystemPictureDetail(Number(params.threadId), { target: 'ARCHITECTURE' }).then((response) => {
			let fileName: string = '';

			if (response.headers["content-disposition"]) {
				fileName = decodeURI(response.headers["content-disposition"]).replace(/.+filename=(.+)\..+$/g, "$1");
			}
			const url = URL.createObjectURL(new Blob([response.data], { type: response.data.type }));
			const link = document.createElement("a");

			link.href = url;
			link.target = "_blank";

			link.download = fileName! || desc.file || "image";

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		});
		// $api({
		// 	url: `/organization/object/${params.threadId}/system/picture`, //your url
		// 	method: "GET",
		// 	params: {
		// 		target: str,
		// 	},
		// 	responseType: "blob", // important
		// }).then((response) => {
		// });
	};
	const getImageStructure = ()=> {
		organizationAPI.objectSystemPictureDetail(Number(params.threadId), { target: 'STRUCTURE' }).then((response) => {
			let fileName: string = '';

			if (response.headers["content-disposition"]) {
				fileName = decodeURI(response.headers["content-disposition"]).replace(/.+filename=(.+)\..+$/g, "$1");
			}
			const url = URL.createObjectURL(new Blob([response.data], { type: response.data.type }));
			const link = document.createElement("a");

			link.href = url;
			link.target = "_blank";

			link.download = fileName! || desc.file || "image";

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		});
		// $api({
		// 	url: `/organization/object/${params.threadId}/system/picture`, //your url
		// 	method: "GET",
		// 	params: {
		// 		target: str,
		// 	},
		// 	responseType: "blob", // important
		// }).then((response) => {
		// });
	};

	const closeAcceptImage = () => {
		setImage((prevState) => ({ ...prevState, file: undefined }));
		setAccept(false);
	};
	const uploadImage = () => {
		organizationAPI.objectSystemDetail(Number(params.threadId)).then(({ data }) => {
			setDesc(data);
			setLoad(false);
			updateTabs("SYSTEM");
			closeAcceptImage();
		});
	};

	useEffect(() => {
		if (params.threadId) {
			organizationAPI.objectSystemGroupsDetail(Number(params.threadId)).then(({ data }) => {
				setTableContent(data);
			});
		}
	}, [params.threadId]);

	// const getTechFromServer = useCallback(() => {
	// 	if (desc.id) {
	// 		$api
	// 			.get<IDescriptionTechTableNew[]>(`/organization/object/system/${desc.id}/means`, {
	// 				params: { part: 1, count: 30 },
	// 			})
	// 			.then((r) => {
	// 				setTechList(r.data);
	// 			});
	// 	}
	// }, [desc.id]);

	const [paramsMean, setParamsMean] = useState({
		part: 1,
		count: 20,
	});

	// useEffect(() => {
	// 	getTechFromServer();
	// }, [desc.id]);
	const wrapperMeans = useRef<HTMLDivElement | null>(null);
	const endMean = useRef<HTMLDivElement | null>(null);
	const thenMean = useCallback(
		(data: typeof techList) => {
			if (paramsMean.part === 1) {
				setTechList(data);
			} else {
				setTechList((prevState) => [...prevState, ...data]);
			}
			if (data.length < paramsMean.count) {
				setIsFinally(true);
			} else {
				setParamsMean((prevState) => ({ ...prevState, part: prevState.part + 1 }));
			}
		},
		//eslint-disable-next-line
		[paramsMean],
	);
	const { setIsFinally, setFetch } = usePaginationWithIntersection({
		link: `/organization/object/system/${desc.id}/means`,
		params: paramsMean,
		ref: wrapperMeans,
		lastTarget: endMean,
		then: thenMean,
		dependencies: [desc.id],
		margin: "200px",
	});

	return (
		<>
			<ConfirmRemoveTechModal
				close={() => {
					setRemoveTech(false);
					setCurrentTech(null);
				}}
				ok={() => {
					currentTech && currentTech.id && removeItemTech(currentTech.id);
					setRemoveTech(false);
				}}
				target={removeTech}
			/>
			<ConfirmRemoveTableItem
				close={() => {
					setRemoveTableItem({ active: false, id: null });
				}}
				ok={() => removeHandlerTable(removeTableItem.id!)}
				target={removeTableItem.active}
			/>
			<ModalTech target={tech} close={() => setTech({ active: false, data: null })} ok={createOrUpdateItemTech} />
			<ModalTable ok={submitHandlerTable} target={table} close={() => setTable({ active: false, data: null })} />
			{accept && image && <AcceptImage image={image} ok={uploadImage} close={closeAcceptImage} />}

			{flag && (
				<PortalWrapper containerRef={container.current}>
					<input
						ref={ref}
						accept={".docx, application/pdf"}
						onChange={(e) => {
							selectFile(e);
						}}
						id={"file"}
						type={"file"}
						disabled={approvedDoc}
						hidden={true}
					/>
					<Button
						onClick={() => {
							checkTables({
								type: "model",
								ok: () => {
									ref.current?.click();
								},
							});
						}}
						disabled={approvedDoc}
						variant={BUTTON_VARIANTS.CONTAINED}>
						Загрузить утвержденный документ
					</Button>
				</PortalWrapper>
			)}

			{load ? (
				<LoadingIcon width={50} height={50} className={parentSass.loading} />
			) : (
				<Accordion>
					<AccordionItem title={"Описание системы"}>
						<div onClick={(e) => e.stopPropagation()} className={sass.acc_desc}>
							<div className={sass.acc_desc__item}>
								{/*<p className={sass.fields_title}>Описание системы</p>*/}
								<div className={sass.fields}>
									<InputTemp
										error={requiredFields && !desc.name}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.name || ""}
										name={"name"}
										title={"Заполните это поле"}
										label={"Наименование системы"}
										placeholder={"Введите наименование системы"}
										required={true}
									/>

									<InputTemp
										error={requiredFields && !desc.security}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.security || ""}
										name={"security"}
										title={"Заполните это поле"}
										placeholder={"Введите текст"}
										label={"Класс защищенности системы"}
										required={true}
									/>

									<TextArea
										rows={6}
										error={requiredFields && !desc.categorySignificance}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.categorySignificance || ""}
										name={"categorySignificance"}
										title={"Заполните это поле"}
										placeholder={"Введите текст"}
										label={"Категория значимости систем и сетей"}
										required={true}
									/>

									<TextArea
										rows={6}
										error={requiredFields && !desc.securityLevel}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.securityLevel || ""}
										name={"securityLevel"}
										title={"Заполните это поле"}
										placeholder={"Введите текст"}
										label={"Уровень защищенности персональных данных"}
										required={true}
									/>

									<TextArea
										rows={6}
										error={requiredFields && !desc.purpose}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.purpose || ""}
										name={"purpose"}
										placeholder={"Введите текст"}
										label={"Назначение, задачи (функции) системы, состав обрабатываемой информации и ее правовой режим"}
										required={true}
									/>
								</div>
							</div>
							<div className={sass.acc_desc__item}>
								{/*<p>&nbsp;</p>*/}
								<div className={sass.fields}>
									<TextArea
										rows={4}
										error={requiredFields && !desc.processes}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.processes || ""}
										name={"processes"}
										placeholder={"Введите текст"}
										label={
											"Основные процессы (бизнес-процессы) обладателя информации, оператора, для обеспечения которых создается (функционирует) система"
										}
										required={true}
									/>

									<TextArea
										rows={2}
										error={requiredFields && !desc.structure}
										required={true}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.structure || ""}
										name={"structure"}
										title={"Заполните это поле"}
										placeholder={"Введите текст"}
										label={"Структура системы"}
									/>
									<AddImage
										key={"STRUCTURE"}
										isStructure={true}
										clickName={(e) => {
											if (currentThread) {
												target ? setTarget(null) : setTarget(e.currentTarget);
											}
										}}
										desc={desc}
										target={target}
										menuRef={menuRefStructure}
										clickMenuRef={(e) => {
											if (confirm) {
												e.preventDefault();
												e.stopPropagation();
												openConfirm();
												return;
											}
										}}
										changeEvent={(e) => {
											if (currentThread) {
												if (e.target.files) {
													setAccept(true);
													setImage({ file: e.target.files[0], mode: "STRUCTURE" });
													e.target.files = null;

													e.target.value = "";
												}
											}
										}}
										getImageFile={getImageStructure}
										addImage={(e) => {
											if (currentThread) {
												if (confirm) {
													openConfirm();
													return;
												} else {
													if (currentThread && e.target.files) {
														setAccept(true);
														setImage({ file: e.target.files[0], mode: "STRUCTURE" });
														e.target.files = null;
														e.target.value = "";
													}
												}
											}
										}}
									/>

									<TextArea
										rows={4}
										error={requiredFields && !desc.architecture}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.architecture || ""}
										name={"architecture"}
										title={"Заполните это поле"}
										placeholder={"Введите текст"}
										label={"Архитектура системы"}
										required={true}
									/>
									<AddImage
										key={"ARCHITECTURE"}
										isStructure={false}
										clickName={(e) => {
											if (currentThread) {
												target ? setTarget(null) : setTarget(e.currentTarget);
											}
										}}
										desc={desc}
										target={target}
										menuRef={menuRefArchitecture}
										clickMenuRef={(e) => {
											if (confirm) {
												e.preventDefault();
												e.stopPropagation();
												openConfirm();
												return;
											}
										}}
										changeEvent={(e) => {
											if (currentThread) {
												if (e.target.files) {
													setAccept(true);
													setImage({ file: e.target.files[0], mode: "ARCHITECTURE" });
													e.target.files = null;

													e.target.value = "";
												}
											}
										}}
										getImageFile={getImageArchitecure}
										addImage={(e) => {
											if (currentThread) {
												if (confirm) {
													openConfirm();
													return;
												} else {
													if (currentThread && e.target.files) {
														setAccept(true);
														setImage({ file: e.target.files[0], mode: "ARCHITECTURE" });
														e.target.files = null;
														e.target.value = "";
													}
												}
											}
										}}
									/>

									<div className={sass.tagsWrapper}>
										<div className={sass.header}>
											<p className={sass.title}>Технические средства системы</p>
											<p
												className={sass.btn}
												onClick={() =>
													currentThread
														? confirm
															? openConfirm()
															: setTech({
																	active: true,
																	data: null,
															  })
														: null
												}>
												Добавить
											</p>
										</div>
										<div ref={wrapperMeans} className={sass.tags}>
											{techList.length ? (
												techList.map((_) => {
													return (
														<div
															key={_.id}
															onClick={() =>
																currentThread
																	? confirm
																		? openConfirm()
																		: setTech({
																				active: true,
																				data: _,
																		  })
																	: null
															}
															className={sass.tags_item}>
															<span>
																{_.type} {_.name} ({fromTable[_.from as keyof typeof fromTable]})
															</span>
														</div>
													);
												})
											) : (
												<span className={sass.tagsEmpty}>Технических средств пока нет, добавьте новое</span>
											)}
											<div ref={endMean} />
										</div>
									</div>

									<TextArea
										rows={2}
										error={requiredFields && !desc.interfaceDescription}
										readOnly={approvedDoc}
										onFocus={focus}
										onBlur={postFetchData}
										onChange={changeField}
										value={desc.interfaceDescription || ""}
										name={"interfaceDescription"}
										placeholder={"Введите текст"}
										label={"Описание внешних интерфейсов и взаимодействий системы с пользователями"}
										required={true}
									/>
								</div>
							</div>
						</div>
					</AccordionItem>
					<AccordionItem
						plusHandler={(e: React.MouseEvent<HTMLDivElement>) => {
							e.stopPropagation();
							confirm ? openConfirm() : setTable({ active: true, data: null });
						}}
						title={"Описание групп пользователей"}>
						<table onClick={(e) => e.stopPropagation()} className={sass.tableRoles}>
							<thead>
								<tr>
									<th>№</th>
									<th>Группа пользователей</th>
									<th>Авторизация</th>
									<th>Категория пользователя</th>
									<th>Уровень полномочий</th>
									<th>Типы доступа</th>
									<th />
								</tr>
							</thead>
							<tbody>
								<Rows
									data={tableContent}
									rowClick={(_) => {
										confirm ? openConfirm() : setTable({ active: true, data: _ });
									}}
									removeClick={(id) =>
										confirm
											? openConfirm()
											: setRemoveTableItem({
													active: true,
													id,
											  })
									}
								/>
							</tbody>
						</table>
					</AccordionItem>
				</Accordion>
			)}
		</>
	);
};

const Rows: React.FC<{
	data: ITableContent[];
	rowClick: (item: ITableContent) => void;
	removeClick: (id: number) => void;
}> = React.memo(({ data, rowClick, removeClick }) => {
	return (
		<>
			{data.map((_, index) => {
				return (
					<tr key={_.id} onClick={() => rowClick(_)}>
						<td>{index + 1}</td>
						<td>{_.name}</td>
						<td>{_.permission}</td>
						<td>{_.category}</td>
						<td>{_.level}</td>
						<td>{_.accessType}</td>
						<td onClick={(e) => e.stopPropagation()}>
							<div onClick={() => removeClick(_.id)} className={sass.dotted}>
								<div />
							</div>
						</td>
					</tr>
				);
			})}
		</>
	);
});

export default DescriptionTable;
